import { type ReactNode } from "react";
import styled, { css } from "styled-components";
import { useDesignTokens } from "@boi/react-design-tokens";

import { Typography } from "../Typography";

export const Container = styled.div`
  ${() => {
    const { tokens } = useDesignTokens();
    const { outerField } = tokens;

    return css`
      border-radius: ${outerField.radius};

      &:has(${Column01} [data-focused]) {
        outline-width: ${outerField.focusRing.width.focus};
        outline-style: ${outerField.focusRing.style};
        outline-color: ${outerField.focusRing.color};
        outline-offset: ${outerField.focusRing.offset.focus};
      }
    `;
  }}
`;

export const InnerContainer = styled.div`
  ${() => {
    const { tokens } = useDesignTokens();
    const { outerField } = tokens;

    return css`
      display: grid;
      grid-template-columns: 1fr;
      grid-auto-flow: column;
      gap: ${outerField.gap};
      min-height: ${outerField.minHeight};
      border-radius: ${outerField.radius};
      outline-width: ${outerField.stroke.width.base};
      outline-style: ${outerField.stroke.style};
      outline-color: ${outerField.stroke.color.base};
      outline-offset: ${outerField.stroke.offset.base};
      background: ${outerField.backgroundColor};
      cursor: text;

      &:has(${Column01} [data-invalid]) {
        outline-width: ${outerField.stroke.width.error};
        outline-color: ${outerField.stroke.color.error};
        outline-offset: ${outerField.stroke.offset.error};
      }

      &:has(${Column01}:hover),
      &:has(${Column02}:hover) {
        outline-width: ${outerField.stroke.width.hover};
        outline-color: ${outerField.stroke.color.hover};
        outline-offset: ${outerField.stroke.offset.hover};
      }

      &:has(${Column01} [data-focused]) {
        outline-width: ${outerField.stroke.width.focus};
        outline-color: ${outerField.stroke.color.focus};
        outline-offset: ${outerField.stroke.offset.focus};
      }

      &:has(${Column01} [data-disabled]) {
        outline-width: ${outerField.stroke.width.base};
        outline-color: ${outerField.stroke.color.disabled};
        outline-offset: ${outerField.stroke.offset.base};
        cursor: not-allowed;
      }
    `;
  }}
`;

type Column01Props = {
  $buttonSlot?: ReactNode;
};

export const Column01 = styled.div<Column01Props>`
  ${({ $buttonSlot }) => {
    const { tokens } = useDesignTokens();
    const { outerField } = tokens;

    return [
      css`
        display: flex;
        align-items: center;
        gap: ${outerField.column01.gap};
        padding-left: ${outerField.column01.padding.left};
        padding-right: ${outerField.column01.padding.right.base};
      `,
      $buttonSlot &&
        css`
          padding-right: ${outerField.column01.padding.right.withButton};
        `,
    ];
  }}
`;

export const StartSlotWrapper = styled(Typography).attrs({ component: "div" })`
  ${() => {
    const { tokens } = useDesignTokens();
    const { outerField } = tokens;

    return css`
      display: grid;
      align-items: center;
      flex-shrink: 0;
      width: ${outerField.column01.startSlot.width};
      height: ${outerField.column01.startSlot.height};
      color: ${outerField.column01.startSlot.color};
      pointer-events: none;
      user-select: none;
      overflow: hidden;
      line-height: 1;

      svg {
        display: block;
        width: ${outerField.column01.startSlot.width};
        height: ${outerField.column01.startSlot.height};
      }
    `;
  }}
`;

export const EndSlotWrapper = styled(Typography).attrs({ component: "div" })`
  ${() => {
    const { tokens } = useDesignTokens();
    const { outerField } = tokens;

    return css`
      display: grid;
      align-items: center;
      flex-shrink: 0;
      width: ${outerField.column01.endSlot.width};
      height: ${outerField.column01.endSlot.height};
      color: ${outerField.column01.endSlot.color};
      pointer-events: none;
      user-select: none;
      overflow: hidden;
      line-height: 1;

      svg {
        display: block;
        width: ${outerField.column01.endSlot.width};
        height: ${outerField.column01.endSlot.height};
      }
    `;
  }}
`;

export const Column02 = styled.div`
  ${() => {
    const { tokens } = useDesignTokens();
    const { outerField } = tokens;

    return css`
      display: grid;
      align-items: center;
      padding-right: ${outerField.column02.padding.right};
    `;
  }}
`;
